/**
 * Mobile-money provider catalogue — integration-ready data layer.
 *
 * Nako Pula serves SMEs across Botswana, South Africa, and Nigeria.
 * Across all three markets customers are migrating off cash and bank
 * accounts onto mobile wallets. To stay relevant we:
 *
 *   1. Show on the landing page that we support the major wallets
 *      ("Mobile money supported"), reassuring prospects from the
 *      first impression.
 *   2. Keep an integration-ready data layer so when the RealPay
 *      mobile-money collection rail goes live we can wire up payouts
 *      and collections without rediscovering the provider list.
 *
 * This file is intentionally JUST data + a thin selector. No HTTP
 * calls, no SDK wiring. Real integration goes into Edge Functions
 * once RealPay confirms which providers their mobile-wallet
 * collection API covers (target: H2 2026).
 *
 * Provider scope (per CFO direction, 2026-05-21):
 *   - BW: Orange Money, MyZaka, FNB eWallet
 *   - ZA: Vodacom M-Pesa, MTN MoMo, FNB eWallet
 *   - NG: OPay, PalmPay, MTN MoMo
 *   - Cross-border: MFS Africa
 *   - Collection: RealPay (Alpha Direct partner)
 */

import type { RegionCode } from './region';

/** Status of the integration for this provider. */
export type MmStatus =
  /** Plumbing in place, awaiting RealPay onboarding. */
  | 'integration_planned'
  /** RealPay has confirmed coverage; awaiting product sign-off. */
  | 'integration_ready'
  /** Live in production. */
  | 'live';

export interface MobileMoneyProvider {
  /** Stable kebab-case id used in DB rows + analytics events. */
  id: string;
  /** Display name as recognised by end users. */
  name: string;
  /** Operator / parent. */
  operator: string;
  /** Country code(s) where the wallet is operationally available. */
  regions: RegionCode[];
  /** Brand colour for the pill / chip render. */
  brandColor: string;
  /** Brand text colour to contrast `brandColor`. */
  brandTextColor: string;
  /** Marketing URL — used by the disclosure footer. */
  url: string;
  /** Current integration status — drives the badge on the landing. */
  status: MmStatus;
  /** True if this rail is a B2B aggregator (covers many providers at once). */
  aggregator?: boolean;
  /** Short description for tooltip / hover. */
  blurb: string;
}

// ────────────────────────────────────────────────────────────────────
// Botswana
// ────────────────────────────────────────────────────────────────────
const BW_PROVIDERS: MobileMoneyProvider[] = [
  {
    id: 'orange-money-bw',
    name: 'Orange Money',
    operator: 'Orange Botswana',
    regions: ['BW'],
    brandColor: '#FF6600',
    brandTextColor: '#FFFFFF',
    url: 'https://www.orange.co.bw/orange-money',
    status: 'integration_planned',
    blurb: 'Botswana&rsquo;s largest mobile-money wallet. USSD + smartphone.',
  },
  {
    id: 'myzaka',
    name: 'MyZaka',
    operator: 'Mascom Wireless',
    regions: ['BW'],
    brandColor: '#0072CE',
    brandTextColor: '#FFFFFF',
    url: 'https://www.mascom.bw/myzaka',
    status: 'integration_planned',
    blurb: 'Mascom&rsquo;s mobile-money service, available on every Mascom SIM.',
  },
];

// ────────────────────────────────────────────────────────────────────
// South Africa
// ────────────────────────────────────────────────────────────────────
const ZA_PROVIDERS: MobileMoneyProvider[] = [
  {
    id: 'vodacom-mpesa-za',
    name: 'M-Pesa',
    operator: 'Vodacom South Africa',
    regions: ['ZA'],
    brandColor: '#E60000',
    brandTextColor: '#FFFFFF',
    url: 'https://www.vodacom.co.za/m-pesa',
    status: 'integration_planned',
    blurb: 'Vodacom relaunched M-Pesa in SA in 2024 with Safaricom.',
  },
  {
    id: 'mtn-momo-za',
    name: 'MTN MoMo',
    operator: 'MTN South Africa',
    regions: ['ZA'],
    brandColor: '#FFCC00',
    brandTextColor: '#000000',
    url: 'https://www.mtn.co.za/momo',
    status: 'integration_planned',
    blurb: 'MTN&rsquo;s mobile-money rail in South Africa.',
  },
];

// ────────────────────────────────────────────────────────────────────
// FNB eWallet — cross-border wallet covering BW + ZA
// ────────────────────────────────────────────────────────────────────
const FNB_EWALLET: MobileMoneyProvider = {
  id: 'fnb-ewallet',
  name: 'FNB eWallet',
  operator: 'First National Bank',
  regions: ['ZA', 'BW'],
  brandColor: '#008C44',
  brandTextColor: '#FFFFFF',
  url: 'https://www.fnb.co.za/ways-to-bank/ewallet/',
  status: 'integration_planned',
  blurb: 'Send cash to any cellphone number — works in both ZA and Botswana.',
};

// ────────────────────────────────────────────────────────────────────
// Nigeria
// ────────────────────────────────────────────────────────────────────
const NG_PROVIDERS: MobileMoneyProvider[] = [
  {
    id: 'opay',
    name: 'OPay',
    operator: 'OPay',
    regions: ['NG'],
    brandColor: '#1FCD68',
    brandTextColor: '#FFFFFF',
    url: 'https://www.opayweb.com',
    status: 'integration_planned',
    blurb: 'One of Nigeria&rsquo;s biggest fintech wallets — Opera-backed.',
  },
  {
    id: 'palmpay',
    name: 'PalmPay',
    operator: 'Transsion Holdings',
    regions: ['NG'],
    brandColor: '#5A3FFF',
    brandTextColor: '#FFFFFF',
    url: 'https://www.palmpay.com',
    status: 'integration_planned',
    blurb: 'Mass-market wallet built on top of low-end Android distribution.',
  },
  {
    id: 'mtn-momo-ng',
    name: 'MTN MoMo',
    operator: 'MTN Nigeria',
    regions: ['NG'],
    brandColor: '#FFCC00',
    brandTextColor: '#000000',
    url: 'https://momo.mtn.ng',
    status: 'integration_planned',
    blurb: 'MTN&rsquo;s mobile-money rail in Nigeria — agent network nationwide.',
  },
];

// ────────────────────────────────────────────────────────────────────
// Cross-border aggregator
// ────────────────────────────────────────────────────────────────────
const REGIONAL_PROVIDERS: MobileMoneyProvider[] = [
  {
    id: 'mfs-africa',
    name: 'MFS Africa',
    operator: 'Onafriq',
    regions: ['BW', 'ZA', 'NG'],
    brandColor: '#0066B3',
    brandTextColor: '#FFFFFF',
    url: 'https://www.onafriq.com',
    status: 'integration_planned',
    aggregator: true,
    blurb: 'Pan-African aggregator — one rail into 200+ mobile-money operators.',
  },
];

// ────────────────────────────────────────────────────────────────────
// RealPay collection partner — Alpha Direct already uses RealPay for
// debit-order collections; their mobile-wallet collection product is
// the upstream rail we plan to plug into.
// ────────────────────────────────────────────────────────────────────
export const COLLECTION_PARTNER = {
  name: 'RealPay Collect Mobile',
  operator: 'RealPay',
  url: 'https://www.realpay.co.za',
  blurb:
    'Existing Alpha Direct partner. RealPay Collect Mobile (in development) ' +
    'is the upstream rail we plan to use to debit customer mobile wallets ' +
    'in BW / ZA / NG without manually integrating each provider one by one.',
};

export const MOBILE_MONEY_PROVIDERS: MobileMoneyProvider[] = [
  ...BW_PROVIDERS,
  ...ZA_PROVIDERS,
  FNB_EWALLET,
  ...NG_PROVIDERS,
  ...REGIONAL_PROVIDERS,
];

/** Providers operational in the given region (excludes B2B aggregators by default). */
export function providersForRegion(
  region: RegionCode,
  opts: { includeAggregators?: boolean } = {},
): MobileMoneyProvider[] {
  const includeAggregators = opts.includeAggregators ?? true;
  return MOBILE_MONEY_PROVIDERS.filter(
    (p) => p.regions.includes(region) && (includeAggregators || !p.aggregator),
  );
}
